import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Loader2, ArrowRight, Check } from 'lucide-react'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { AuthSplit, FormField, FormNote, formLinkClass } from '@/components/landing/EditorialForm'
import { authService } from '@/lib/api/services/auth'
import { useToast } from '@/components/ui/use-toast'

export default function RegisterPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      })
      return
    }

    try {
      setLoading(true)
      await authService.register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      })
      toast({
        title: 'Success',
        description: 'Account created successfully. Please login.',
      })
      navigate('/login')
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to create account. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const set = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [field]: e.target.value })

  return (
    <LandingLayout>
      <AuthSplit
        tone="purple"
        title="Start free."
        lede={
          <p>
            Already have an account?{' '}
            <Link to="/login" className={formLinkClass}>
              Sign in
            </Link>
          </p>
        }
        aside={
          <>
            <p className="landing-text-display-xl">
              14 days free.
              <br />
              No card needed.
            </p>
            <ul className="max-w-md space-y-4">
              {[
                'Unlimited templates',
                'AI-powered content generation',
                'Microsoft Office integration',
                'Team collaboration',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3 border-b border-white/20 pb-4 text-[17px]">
                  <Check className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                  {feature}
                </li>
              ))}
            </ul>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-2">
          <FormField id="firstName" label="First name">
            <input id="firstName" className="landing-field" placeholder="First name" autoComplete="given-name" required value={formData.firstName} onChange={set('firstName')} />
          </FormField>
          <FormField id="lastName" label="Last name">
            <input id="lastName" className="landing-field" placeholder="Last name" autoComplete="family-name" required value={formData.lastName} onChange={set('lastName')} />
          </FormField>
          <FormField id="email" label="Email address" className="sm:col-span-2">
            <input id="email" type="email" className="landing-field" placeholder="Your work email" autoComplete="email" required value={formData.email} onChange={set('email')} />
          </FormField>
          <FormField id="password" label="Password" className="sm:col-span-2">
            <input id="password" type="password" className="landing-field normal-case" placeholder="Password" autoComplete="new-password" required value={formData.password} onChange={set('password')} />
          </FormField>
          <FormField id="confirmPassword" label="Confirm password" className="sm:col-span-2">
            <input id="confirmPassword" type="password" className="landing-field normal-case" placeholder="Confirm password" autoComplete="new-password" required value={formData.confirmPassword} onChange={set('confirmPassword')} />
          </FormField>

          <div className="sm:col-span-2 mt-6 flex flex-col gap-4">
            <button
              type="submit"
              disabled={loading}
              className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep w-full disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating account
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
            <FormNote>
              By creating an account you agree to our{' '}
              <Link to="/terms" className={formLinkClass}>Terms of Service</Link> and{' '}
              <Link to="/privacy" className={formLinkClass}>Privacy Policy</Link>.
            </FormNote>
          </div>
        </form>
      </AuthSplit>
    </LandingLayout>
  )
}
